import * as Models from "../../Models";
import * as Services from "../../Services";
import * as Shared from "../../Shared";
import * as Routes from "../../Routes";
import * as Common from "../../Common/modules";
import * as toastr from "toastr";
import * as _ from "lodash";
import * as moment from "moment";
import { PrepaidSrecCashflowPercent } from "../../Models/CostCalculation/MarketAssumedValuesModel";

export class CostCalculationAssumedValuesCtrl {
    public assumedValuesModel: Models.CostCalculation.MarketAssumedValuesModel.IMarketAssumedValuesModel;

    public isSrecIncentiveType: boolean;
    public isPbiIncentiveType: boolean;
    public isCbiIncentiveType: boolean;
    public isPrepaidSrecIcentiveType: boolean;
    public hasNoIncentive: boolean;
    public selectedIncentiveType: Models.CostCalculation.IncentiveType.IIncentiveType;
    public allIncentiveTypes: Models.CostCalculation.IncentiveType.IIncentiveType[];

    public selectedMarket: Models.CostCalculation.Market.Market;
    public possibleMarkets: Models.CostCalculation.Market.IMarket[];
    public allMarkets: Models.CostCalculation.Market.IMarket[];
    public marketTypes: Models.CostCalculation.MarketType.IMarketType[];

    public tabStatus = new Models.CostCalculation.AssumedValuesTabGroup.AssumedValuesTabGroup();

    public availableSrecPriceCurves: Models.CostCalculation.MarketAssumedValuesModel.ISrecPriceCurves[];
    public srecPriceCurves: Models.CostCalculation.MarketAssumedValuesModel.ISrecPriceCurves[];

    public srecSellingOffsetOptions: Models.CostCalculation.SrecOffsetMonthOption.SrecOffsetMonthOption[];

    public readonly monthMap = ['START_AT_INDEX_1', 'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

    public readonly sampleRates = [0.09, 0.10, 0.11, 0.12, 0.13, 0.14, 0.15, 0.16];
    public readonly samplePercentYields = [1000, 1100, 1200, 1300];

    public static $inject = ['$modal', 'ngToast', '$state', 'CostCalculationApiClient', `MarketAssumedValuesApiClient`, `SrecPriceCurveApiClient`, 'igs.messageBox'];
    constructor(public $modal: angular.ui.bootstrap.IModalService,
        public ngToast: any,
        public $state: angular.ui.IStateService,
        public costCalculationApiClient: Services.CostCalculationApiClient.ICostCalculationApiClient,
        public marketAssumedValuesApiClient: Services.MarketAssumedValuesApiClient.IMarketAssumedValuesApiClient,
        public srecPriceCurveApiClient: Services.SrecPriceCurveApiClient.ISrecPriceCurveApiClient,
        private messageBox: any) {

        this.setupAllMarkets();
        this.loadMarketTypes();
        this.setupSrecPriceCurves();
    }

    public getEpcAssumedValuesForMarket = (): void => {

        this.marketAssumedValuesApiClient.getAssumedValuesForMarket(this.selectedMarket.code)
            .then((data: Models.CostCalculation.MarketAssumedValuesModel.IMarketAssumedValuesModel) => {
                this.updateSrecSellingMonthOffsetsWithServerData(data.relativeSrecSellingMonths);

                data.acceleratedDepreciationPoints = _.sortBy(data.acceleratedDepreciationPoints,
                    (point) => {
                        return point.term;
                    });

                this.assumedValuesModel = data;
                this.selectedMarket.name = data.market.name;
                this.assumedValuesModel.market.marketTypeCode = _.find(this.marketTypes, x => x.code === data.market.marketTypeCode).code;

                this.selectedIncentiveType = this.assumedValuesModel.incentiveType;
                this.setIncentiveType();

                if (!this.assumedValuesModel.inUse) {
                    this.setModelPriceCurves(this.availableSrecPriceCurves, data);
                } else {
                    this.setModelPriceCurves(this.srecPriceCurves, data);
                }

                this.convertDateStringsToDateObjectsOnActiveAssumedValuesModel();
            });

        this.tabStatus = new Models.CostCalculation.AssumedValuesTabGroup.AssumedValuesTabGroup();
    }

    public addPrepaidSrecCashflowPercent = (): void => {
        this.assumedValuesModel.prepaidSrecCashflowPercents.push(new PrepaidSrecCashflowPercent());
    }
  
    public removePrepaidSrecCashflowPercent = (cashflowPercent: PrepaidSrecCashflowPercent): void => {
        _.remove(this.assumedValuesModel.prepaidSrecCashflowPercents, (cp) => cp === cashflowPercent); // this is doing a reference comparison, not value comparison
    }

    public validCashflowPercents = (): boolean => {
        const sum = this.assumedValuesModel.prepaidSrecCashflowPercents.reduce((acc, currentValue) => (acc + currentValue.srecPercent), 0);
        return sum <= 1;
    }

    public validCashflows = (): boolean => {
        const cashflows = this.assumedValuesModel.prepaidSrecCashflowPercents.map((x) => x.cashflowNumber);
        const uniqueCashflows = new Set(this.assumedValuesModel.prepaidSrecCashflowPercents.map((x) => x.cashflowNumber));
        return cashflows.length === uniqueCashflows.size;
    }

    public setIncentiveType = () => {
        const incentiveCode = this.selectedIncentiveType.code;

        //todo: remove hardcoding
        switch (incentiveCode) {
            case 'SREC':
                this.isSrecIncentiveType = true;
                this.hasNoIncentive = false;
                this.isPbiIncentiveType = false;
                this.isCbiIncentiveType = false;
                this.isPrepaidSrecIcentiveType = false;
                break;
            case 'PBI': 
                this.isPbiIncentiveType = true;
                this.hasNoIncentive = false;
                this.isSrecIncentiveType = false;
                this.isCbiIncentiveType = false;
                this.isPrepaidSrecIcentiveType = false;
                break;
            case 'CBI': 
                this.isPbiIncentiveType = false;
                this.hasNoIncentive = false;
                this.isSrecIncentiveType = false;
                this.isCbiIncentiveType = true;
                this.isPrepaidSrecIcentiveType = false;
                break;
            case 'PREPAIDSREC':
                this.isPbiIncentiveType = false;
                this.hasNoIncentive = false;
                this.isSrecIncentiveType = false;
                this.isCbiIncentiveType = false;
                this.isPrepaidSrecIcentiveType = true;
                break;
            case 'NONE':
                this.isSrecIncentiveType = false;
                this.hasNoIncentive = true;
                this.isPbiIncentiveType = false;
                this.isCbiIncentiveType = false;
                this.isPrepaidSrecIcentiveType = false;
                break;
            default:
                this.isSrecIncentiveType = false;
                this.hasNoIncentive = false;
                this.isPbiIncentiveType = false;
                this.isCbiIncentiveType = false;
                this.isPrepaidSrecIcentiveType = false;
                break;
        }

        this.assumedValuesModel.incentiveType = this.selectedIncentiveType;
    }

    public setModelPriceCurves = (srecPriceCurves: Models.CostCalculation.MarketAssumedValuesModel.ISrecPriceCurves[], data: Models.CostCalculation.MarketAssumedValuesModel.IMarketAssumedValuesModel) => {
        if (data.tier1SrecCurve != null && _.find(srecPriceCurves, x => x.code === data.tier1SrecCurve.code)) {
            this.assumedValuesModel.tier1SrecCurve.name = _.find(srecPriceCurves, x => x.code === data.tier1SrecCurve.code).name;
        } else {
            this.assumedValuesModel.tier1SrecCurve = null;
        }

        if (data.tier2SrecCurve != null) {
            if (_.find(srecPriceCurves, x => x.code === data.tier2SrecCurve.code)) {
                this.assumedValuesModel.tier2SrecCurve.name = _.find(srecPriceCurves, x => x.code === data.tier2SrecCurve.code).name;
            } else {
                this.assumedValuesModel.tier2SrecCurve = null;
            }
        }
    }

    public openCloneMarketModal = () => {
        var modalInstance = this.$modal.open({
            template: require("./Modals/cloneMarket.html"),
            controller: Shared.Framework.ControllerHelpers.create(Routes.CostCalculation.modals.cloneMarket.Igs.Solar.CostCalculation
                    .CloneMarketCtrl),
            size: 'lg',
            resolve: {
                assumedValuesModel: () => {
                    return this.assumedValuesModel;
                }
            }
        });

        modalInstance.result.then((clonedAssumedValuesModel: Models.CostCalculation.MarketAssumedValuesModel.IMarketAssumedValuesModel) => {
            this.allMarkets.push(clonedAssumedValuesModel.market);
            this.selectedMarket = clonedAssumedValuesModel.market;
            this.assumedValuesModel = clonedAssumedValuesModel;

            this.getEpcAssumedValuesForMarket();
        });
    }

    public removeSrecPriceInterval = (srecPriceInterval: Models.CostCalculation.MarketAssumedValuesModel.ISrecPriceInterval): void => {
        this.assumedValuesModel.srecPriceIntervals = _.remove(this.assumedValuesModel.srecPriceIntervals,
            (currentObject: Models.CostCalculation.MarketAssumedValuesModel.ISrecPriceInterval) => {
                return currentObject.startDate !== srecPriceInterval.startDate;
            });
    }

    public removeAcceleratedDepreciationPoint = (accelDepProint: Models.CostCalculation.MarketAssumedValuesModel.IAcceleratedDepreciationPoints): void => {
        this.assumedValuesModel.acceleratedDepreciationPoints = _.remove(this.assumedValuesModel
            .acceleratedDepreciationPoints,
            (currentObject) => {
                return currentObject.term !== accelDepProint.term;
            });
    }

    public updateEpcAssumedValuesForMarket = (): void => {
        var openBox = this.messageBox.open('EPC Assumed Market Values',
            'Are you sure you want to update the EPC Assumed Market Values? This will impact all calculations in this market.',
            [{ label: 'Submit', cssClass: 'btn-primary', result: true }, { label: 'Cancel', cssClass: 'btn-cancel', result: false }]);

        openBox.then((result: boolean) => {
            if (result) {
                this.assumedValuesModel.relativeSrecSellingMonths = this.translateSrecSellingOptionsToRelativeSellingMonths(this.srecSellingOffsetOptions);

                this.marketAssumedValuesApiClient.updateEpcAssumedValuesForMarket(this.assumedValuesModel).then((response: Models.BaseApiResponse.BaseApiResponse) => {
                    if (response.success) {
                        this.getEpcAssumedValuesForMarket();
                        toastr.success('The EPC Assumed Values have been updated for this market', 'Success!');
                    } else {
                        const errorString = response.errors.reduce((strOne, strTwo) => strOne + strTwo + '\r\n');
                        toastr.error(errorString);
                    }
                },
                    () => {
                        toastr.error('Something went wrong when attempting to update the EPC Assumed Values. Please try again or contact IT if the problem continues.', 'ERROR!');
                    });
            };
        });
    }

    public addNewAccelDepPoint = (): void => {
        var accelDepPricePoint = new Models.CostCalculation.MarketAssumedValuesModel.AcceleratedDepreciationPoints();
        accelDepPricePoint.term = this.getMaxTermFromAccelDepPricePoints() + 1;
        this.assumedValuesModel.acceleratedDepreciationPoints.push(accelDepPricePoint);
    }

    public getMaxTermFromAccelDepPricePoints = (): number => {
        var maxTerm = 0;

        _.forEach(this.assumedValuesModel.acceleratedDepreciationPoints,
            (accelDepPricePoint) => {
                if (accelDepPricePoint.term > maxTerm) {
                    maxTerm = accelDepPricePoint.term;
                }
            });

        return maxTerm;
    }

    public sortDepreciationPointsByTerm = (): void => {
        this.assumedValuesModel.acceleratedDepreciationPoints.sort((p1, p2) => p1.term - p2.term);
    }

    public setupAllMarkets = (): void => {
        this.marketAssumedValuesApiClient.getMarkets().then((result: Models.CostCalculation.Market.Market[]) => {
            this.allMarkets = result;
        });
    }

    public setupSrecPriceCurves = (): void => {
        this.srecPriceCurveApiClient.getSrecPriceCurves().then((data: Models.CostCalculation.MarketAssumedValuesModel.SrecPriceCurves[]) => {
            this.srecPriceCurves = data;
            this.availableSrecPriceCurves = data.filter(x => x.availableForMarketAssociation);
        });
    }

    public loadMarketTypes = (): void => {
        this.marketAssumedValuesApiClient.getMarketTypes()
            .then((result: Models.CostCalculation.MarketType.MarketType[]) => {
                this.marketTypes = result;
            });
    }

    private updateSrecSellingMonthOffsetsWithServerData = (currentOffsets: number[]): void => {
        this.srecSellingOffsetOptions = [];

        for (let i = 1; i <= 12; i++) {
            this.srecSellingOffsetOptions.push(new Models.CostCalculation.SrecOffsetMonthOption.SrecOffsetMonthOption(i, _.includes(currentOffsets, i)));
        }
    }

    private translateSrecSellingOptionsToRelativeSellingMonths = (options: Models.CostCalculation.SrecOffsetMonthOption.SrecOffsetMonthOption[]): number[] => {
        return options
            .filter(option => option.selected)
            .map(option => option.offsetMonth);
    }

    public activeMarketIsPilotPricing = (): boolean => {
        return this.assumedValuesModel.market.marketTypeCode === 'PP';
    }

    public activeMarketIsSingleInvestorPricing = (): boolean => {
        return this.assumedValuesModel.market.marketTypeCode === 'SIP';
    }

    public activeMarketIsMultiInvestorPricing = (): boolean => {
        return this.assumedValuesModel.market.marketTypeCode === 'MIP';
    }

    // C# returns DateTimes as a string in a format like 2019-02-05T15:50:55.633
    // The jQuery DateTimePicker returns a javascript Date object
    // Ignore comments below this line.
    // This disparity causes comparisons between dates returned from the server and update dates to fail.
    // This method converts all date strings returned from the server that are used in comparisons to Date objects
    // The date that is returned from the server is in Zulu time. We need to convert it from a string in zulu time format to a date in eastern time.
    private convertDateStringsToDateObjectsOnActiveAssumedValuesModel = (): void => {
        this.assumedValuesModel.market.displayStartDate = this.assumedValuesModel.market.displayStartDate ? new Date(this.assumedValuesModel.market.displayStartDate) : null;
        this.assumedValuesModel.market.displayEndDate = this.assumedValuesModel.market.displayEndDate ? new Date(this.assumedValuesModel.market.displayEndDate) : null;
        this.assumedValuesModel.market.effectiveStartDate = this.assumedValuesModel.market.effectiveStartDate ? new Date(this.assumedValuesModel.market.effectiveStartDate) : null;
        this.assumedValuesModel.market.effectiveEndDate = this.assumedValuesModel.market.effectiveEndDate ? new Date(this.assumedValuesModel.market.effectiveEndDate) : null;
        this.assumedValuesModel.defaultContractSignDate = this.assumedValuesModel.defaultContractSignDate ? new Date(this.assumedValuesModel.defaultContractSignDate) : null;

        if(this.assumedValuesModel.srecPriceIntervals != null){
            this.assumedValuesModel.srecPriceIntervals.forEach(interval => {
                interval.startDate = new Date(interval.startDate);
                interval.endDate = new Date(interval.endDate);
            });
        }
        
    }

    private intervalSort = (i1: Models.CostCalculation.MarketAssumedValuesModel.SrecPriceInterval, i2: Models.CostCalculation.MarketAssumedValuesModel.SrecPriceInterval): number => {
        if (i1.endDate > i2.endDate) return 1;
        if (i1.endDate < i2.endDate) return -1;
        return 0;
    }
}